import { Autocomplete, MenuItem, SelectProps, TextField, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { QuestionnaireItemAnswerOption } from 'fhir/r4b';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { GetAnswerOptionsRequest } from 'utils/lib/types/data/telemed/appointments/appointments.types';
import { AnswerLoadingOptions } from '../../../../../config-types/config/fhir';
import { usePaperworkContext } from '../context';
import { usePaperworkOtherColors } from '../theme';
import { VirtualizedListbox } from './VirtualizedListbox';

type PrunedSelectProps = Omit<
  SelectProps<HTMLTextAreaElement>,
  'onKeyDown' | 'onKeyUp' | 'onChange' | 'onBlur' | 'onFocus' | 'onInvalid' | 'componentsProps' | 'slotProps'
>;
type FreeMultiSelectInputProps = {
  name: string;
  value: string | string[] | undefined;
  options: QuestionnaireItemAnswerOption[];
  placeholder?: string;
  virtualization?: boolean;
  freeSolo?: boolean;
  multiple?: boolean;
  dynamicAnswerOptions?: AnswerLoadingOptions;
  answerValueSet?: string;
  onChange: (e: any) => void;
} & PrunedSelectProps;

export const FreeMultiSelectInput: FC<FreeMultiSelectInputProps> = ({
  name,
  options: staticOptions,
  freeSolo,
  disabled,
  placeholder: customPlaceholder,
  multiple,
  virtualization = true,
  dynamicAnswerOptions,
  answerValueSet,
  onChange,
  ...otherProps
}) => {
  const theme = useTheme();
  const otherColors = usePaperworkOtherColors();
  const [inputValue, setInputValue] = useState<string>('');

  const fetchOptionsInput: GetAnswerOptionsRequest | undefined = (() => {
    if (dynamicAnswerOptions !== undefined) {
      const { strategy, answerSource } = dynamicAnswerOptions;
      if (strategy !== 'dynamic') {
        return undefined;
      } else {
        if (answerSource !== undefined) {
          return {
            answerSource,
          };
        } else if (answerValueSet !== undefined) {
          return {
            answerValueSet,
          };
        }
      }
    }
    return undefined;
  })();
  const usesDynamicOptions = fetchOptionsInput !== undefined;
  const valueType = dynamicAnswerOptions?.answerSource !== undefined ? 'Reference' : 'String';

  const { paperworkComponentHelpers } = usePaperworkContext();

  const { data } = useQuery({
    queryKey: [name, fetchOptionsInput],
    queryFn: () => paperworkComponentHelpers.getAnswerOptions?.(fetchOptionsInput as GetAnswerOptionsRequest),
    enabled: usesDynamicOptions && paperworkComponentHelpers.getAnswerOptions !== undefined,
  });

  const { getValues } = useFormContext();

  const defaultOrNull: any = multiple ? [] : null;
  const placeholder = (() => {
    if (customPlaceholder) {
      return customPlaceholder;
    }
    if (freeSolo && multiple) {
      return 'Type or select all that apply';
    }
    if (freeSolo) {
      return 'Type or select...';
    }
    if (multiple) {
      return 'Select all that apply...';
    }
    return 'Select...';
  })();

  const selectionHandler = useCallback(
    (e: any): void => {
      const targetVal = e?.selectedOption ?? e?.target?.value;
      if (multiple && targetVal) {
        const value = getValues(name)?.answer?.map((i: any) => i.valueString) ?? [];
        const newVal = [...value, targetVal];
        onChange({ target: { value: newVal } });
      } else {
        onChange({ target: { value: targetVal } });
      }
      setInputValue('');
    },
    [getValues, multiple, name, onChange]
  );

  const options = useMemo(() => {
    let baseOptions: QuestionnaireItemAnswerOption[] = [];
    if (usesDynamicOptions) {
      baseOptions = data ?? [];
    } else {
      baseOptions = staticOptions;
    }

    if (!multiple) {
      return moveOtherOptionToEnd(baseOptions);
    }

    const selectedValues = new Set(
      Array.isArray(otherProps.value)
        ? (otherProps.value as any[]).map((val) => {
            if (valueType === 'String') {
              return typeof val === 'string' ? val : val?.valueString ?? val;
            }
            return val?.reference ?? val?.valueReference?.reference ?? val;
          })
        : []
    );

    const filteredOptions = baseOptions.filter((option) => {
      if (option?.valueReference?.type === 'other') {
        return true;
      }
      if (valueType === 'String') {
        return option.valueString && !selectedValues.has(option.valueString);
      }
      const referenceValue = option?.valueReference?.reference;
      return referenceValue && !selectedValues.has(referenceValue);
    });

    return moveOtherOptionToEnd(filteredOptions);
  }, [usesDynamicOptions, data, multiple, otherProps.value, staticOptions, valueType]);

  return (
    <Autocomplete
      {...otherProps}
      sx={{
        '& ': {
          margin: '0px',
        },
        '& .MuiAutocomplete-iconOpen': {
          marginRight: '10px',
        },
        '& .MuiFilledInput-root': {
          fontSize: 16,
          padding: 0.25,
          backgroundColor: `${theme.palette.background.paper}`,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: otherColors.lightGray,
          '&::before, ::after, :hover:not(.Mui-disabled, .Mui-error)::before': {
            borderBottom: 0,
          },
        },
        '& .Mui-focused': {
          boxShadow: `${otherColors.primaryBoxShadow} 0 0 0 0.2rem`,
          borderColor: `${theme.palette.primary.main}`,
        },
        '& .Mui-disabled': {
          backgroundColor: 'white',
        },
      }}
      autoComplete
      value={otherProps.value || null}
      disableClearable={otherProps.required}
      disabled={disabled}
      id={name}
      options={options}
      filterOptions={(options, state) => {
        const { inputValue, getOptionLabel } = state;
        const filtered = options.filter((option) => {
          return (
            getOptionLabel(option)
              ?.toLowerCase()
              ?.startsWith(inputValue?.toLowerCase()) || option?.valueReference?.type === 'other'
          );
        });
        return filtered;
      }}
      freeSolo={freeSolo}
      fullWidth
      ListboxComponent={virtualization ? VirtualizedListbox : undefined}
      multiple={multiple}
      onChange={selectionHandler}
      onBlur={() => freeSolo && inputValue && selectionHandler({ selectedOption: inputValue })}
      defaultValue={defaultOrNull}
      renderTags={(_options, _getTagProps) => null}
      getOptionLabel={(option) => {
        return labelForOption(option);
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'object') {
          if (valueType === 'String') {
            return option.valueString === value;
          } else {
            return option?.valueReference?.reference === value?.reference;
          }
        } else {
          return option === value;
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderOption={
        virtualization
          ? (props, option, state) => {
              const onClickCopy = { ...props }.onClick;
              const newOnClick = (e: any): void => {
                onClickCopy?.({ ...e, selectedOption: valueForOption(option, valueType) } as any);
              };
              const propsCopy = { ...props, onClick: newOnClick };
              return [propsCopy, { id: idForOption(option), label: labelForOption(option) }, state.index] as ReactNode;
            }
          : (props, option, _state) => {
              return (
                <MenuItem
                  key={idForOption(option)}
                  value={valueForOption(option, valueType)}
                  sx={{
                    whiteSpace: 'normal',
                  }}
                  onClick={(e) => {
                    props?.onClick?.({ ...e, selectedOption: valueForOption(option, valueType) } as any);
                  }}
                >
                  {labelForOption(option)}
                </MenuItem>
              );
            }
      }
      renderInput={(params) => {
        return <TextField {...params} variant="filled" placeholder={placeholder} />;
      }}
    />
  );
};

const labelForOption = (option: any): string => {
  if (option?.valueString !== undefined) {
    return option.valueString;
  } else if (option?.valueReference?.display !== undefined) {
    return `${option?.valueReference?.display}`;
  } else if (option?.display !== undefined) {
    return `${option.display}`;
  }
  return `${option}`;
};

const idForOption = (option: any): string => {
  return option.id ?? option.valueString ?? option.valueReference?.reference ?? `${option}`;
};

const valueForOption = (option: any, valueType: 'String' | 'Reference'): any => {
  const defaultVal = valueType === 'String' ? '' : null;
  const value = option?.[`value${valueType}`] ?? defaultVal;
  if (valueType === 'Reference' && value?.type === 'other') {
    const { type: _type, ...rest } = value;
    return rest;
  }
  return value;
};

const moveOtherOptionToEnd = (options: QuestionnaireItemAnswerOption[]): QuestionnaireItemAnswerOption[] => {
  const otherOptionIndex = options.findIndex((option) => option?.valueReference?.type === 'other');
  if (otherOptionIndex === -1) {
    return options;
  }
  const otherOption = options[otherOptionIndex];
  return [...options.slice(0, otherOptionIndex), ...options.slice(otherOptionIndex + 1), otherOption];
};
